import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

// Read more on the Picture Element: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/picture

/*
imageConfig structure

imageConfig: {
    src: '', // Required | Image url
    srcSet: [{src: "url with overrides & custom optimizations if any", media: "(min-width: 600px)", type: "image/webp" }], // Optional: Default - undefined | Set srcset
    height: "500", // Optional: Default - undefined | Set height for the image
    width: "500", // Optional: Default - undefined | Set width for the image
    loading: "eager", // Optional: Default - undefined | Set loading type (eager/lazy)
    category: "HERO" // Optional: Default - undefined | Set Category type for images to apply category level optimizations
    alt: '' // Optional: Default - '' | Set alt text for the image
  }
*/

const DEFAULT_OPTIMIZATIONS = {
  auto: 'webp', // will transform the image to webp format if the browser supports webp
  strip: '1' // will remove metadata for `edgio` served images, `fastly` removes metadata by default
};

const CATEGORY_TYPES = {
  hero: 'HERO',
  card: 'CARD'
};

const CATEGORY_OPTIMIZATIONS = {
  [CATEGORY_TYPES.card]: {
    quality: '80' // will compress images to by 20% to 80% of original quality
  }
};

const optimizeURL = (url, category) => {
  let urlComponents;
  try {
    urlComponents = new URL(url);
  } catch {
    return url; // return the url as is if the URL Constructor fails for relative paths or incorrect urls
  }

  const { searchParams } = urlComponents;

  function applyOptimizations(optimizations) {
    Object.entries(optimizations).forEach(([key, val]) => {
      if (searchParams.has(key)) {
        return;
      }
      searchParams.append(key, val);
    });
  }

  applyOptimizations(DEFAULT_OPTIMIZATIONS);

  if (category) {
    if (CATEGORY_OPTIMIZATIONS[category]) {
      applyOptimizations(CATEGORY_OPTIMIZATIONS[category]);
    }
  }

  return urlComponents.toString();
};

const Image = ({ imageConfig, className }) => {
  const { src, alt, srcSet, width, height, loading, category } = imageConfig;

  const optimizedSrc = useMemo(() => {
    if (!src) return '';

    return optimizeURL(src, category);
  }, [src]);

  const optimizedSrcset = useMemo(() => {
    if (!srcSet) return [];

    return srcSet.reduce((acc, cur) => {
      if (!cur.media && !cur.type) return acc;

      return [...acc, { ...cur, src: optimizeURL(cur.src, category) }];
    }, []);
  }, [srcSet]);

  return (
    <picture className={className}>
      {optimizedSrcset.length !== 0 &&
        optimizedSrcset.map((srcSetItem, i) => (
          <source srcSet={srcSetItem.src} media={srcSetItem.media} type={srcSetItem.type} key={i} />
        ))}
      <img src={optimizedSrc} alt={alt} width={width} height={height} loading={loading} />
    </picture>
  );
};

Image.propTypes = {
  className: PropTypes.string,
  imageConfig: PropTypes.object
};

Image.defaultProps = {
  className: '',
  imageConfig: {
    src: '',
    srcSet: undefined,
    height: undefined,
    width: undefined,
    loading: undefined,
    alt: ''
  }
};

export default Image;
